import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListDunningStepsResult, type RunDunningResult } from '@whinerp/client';
import { Button, EmptyState, ErrorState, PageHeading, Panel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';
import { defineScreen } from '../types.js';

/**
 * SC-76 Chasing overdue invoices (G-130).
 *
 * The screen states the two limits the ladder imposes, because somebody
 * setting it up needs to know them before they design the wording:
 *
 * One rung per run. An invoice found ninety days late does not produce four
 * letters in a customer's inbox in one morning.
 *
 * Nothing here cancels anything. The last rung raises the matter for a
 * person, and no rung that cuts a customer off can be configured at all.
 *
 * The run's outcomes are shown in full, including what it declined to do.
 * "3 sent" on its own hides the account nobody can write to.
 */

function DunningScreen(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const [run, setRun] = useState<RunDunningResult | null>(null);

  const steps = useQuery<ListDunningStepsResult, ApiError>({
    queryKey: ['dunning-steps'],
    queryFn: () => api.listDunningSteps({}),
  });

  const chase = useMutation<RunDunningResult, ApiError>({
    mutationFn: () => api.runDunning({ body: {} }),
    onSuccess: (result) => {
      setRun(result);
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });

  if (steps.isPending) return <Loading />;
  if (steps.isError) {
    return (
      <div data-component="Dunning">
        <ErrorState title={t('state.error')} body={errorText(steps.error)} onRetry={() => void steps.refetch()} />
      </div>
    );
  }

  const rungs = steps.data.steps;

  return (
    <div data-component="Dunning" className="flex w-full flex-col gap-4">
      <PageHeading>{t('dunning.title')}</PageHeading>
      <p className="text-body text-ink-meta">{t('dunning.help')}</p>
      {/* Said on the screen, not only in the code: whoever writes the wording
          needs to know the ladder cannot cut anyone off. */}
      <p className="text-dense text-ink-600" data-dunning-limits>
        {t('dunning.limits')}
      </p>

      {rungs.length === 0 ? (
        <EmptyState title={t('dunning.empty')} body={t('dunning.empty.help')} />
      ) : (
        <Panel>
          <ol className="flex flex-col gap-2 text-dense">
            {rungs.map((step) => (
              <li key={step.stepId} data-dunning-step={step.stepId} className="flex flex-wrap items-baseline gap-2">
                <span className="text-ink">{t('dunning.after', { values: { days: String(step.afterDays) } })}</span>
                <span className="text-ink-600" data-dunning-action={step.action}>
                  {t(`dunning.action.${step.action}`)}
                </span>
                {step.subject ? <span className="text-meta text-ink-meta">{step.subject}</span> : null}
                {!step.active ? <span className="text-meta text-ink-meta">{t('dunning.inactive')}</span> : null}
              </li>
            ))}
          </ol>
        </Panel>
      )}

      {can('revenue.manage') ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button intent="primary" size="small" data-dunning-run onClick={() => chase.mutate()} disabled={chase.isPending}>
            {t('dunning.run')}
          </Button>
          {chase.error ? <span className="text-dense text-danger-text">{errorText(chase.error)}</span> : null}
          {run ? (
            <span className="text-dense text-ink-meta" data-dunning-run-result>
              {t('dunning.run.result', {
                values: {
                  sent: String(run.sent),
                  notified: String(run.notified),
                  escalated: String(run.escalated),
                  skipped: String(run.skipped),
                },
              })}
            </span>
          ) : null}
        </div>
      ) : null}

      {run && run.outcomes.length > 0 ? (
        <Panel data-dunning-outcomes>
          <ul className="flex flex-col gap-1 text-dense text-ink-600">
            {run.outcomes.map((outcome) => (
              <li key={outcome.invoiceId} data-dunning-outcome={outcome.action ?? 'none'}>
                {/* The detail, always: "3 sent" on its own hides the account
                    nobody can write to. */}
                {outcome.detail}
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
    </div>
  );
}

function Loading(): JSX.Element {
  return (
    <div data-component="Dunning">
      <Skeleton className="h-plot w-full" />
    </div>
  );
}

function Empty(): JSX.Element {
  const t = useT();
  return (
    <div data-component="Dunning">
      <EmptyState title={t('dunning.empty')} body={t('dunning.empty.help')} />
    </div>
  );
}

function Failed(): JSX.Element {
  const t = useT();
  return (
    <div data-component="Dunning">
      <ErrorState title={t('state.error')} body={t('dunning.help')} />
    </div>
  );
}

export const screen = defineScreen({
  id: 'SC-76',
  route: '/revenue/dunning',
  surface: 'page',
  title: 'Chasing overdue invoices',
  features: ['F-080'],
  entitlement: 'F-080',
  reachedBy: [{ kind: 'screen', from: 'SC-75', how: 'Open the chasing ladder from invoices' }],
  components: [],
  elements: ['Dunning'],
  states: { empty: Empty, loading: Loading, error: Failed },
  component: DunningScreen,
});

export default screen;
